import type { UseQueryResult } from "react-query";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { AnalyticsGA } from "../../utils/AnalyticsGA";
import apiBase from "../../utils/apiBase";

export interface IRequestParams {
  organizationId: string;
  pageIndex?: number;
  pageSize?: number;
  search?: string;
  type?: string;
}

export interface IQueryParams {
  pageIndex?: number;
  pageSize?: number;
  search?: string;
}

interface IOwner {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface IEditQueryParams {
  organizationId: string;
  keyId: string;
  name: string;
  state: string;
}

export interface IKey {
  id: string;
  name: string;
  key?: string;
  owner?: IOwner;
  applicationId: string;
  state?: string;
  expirationDateTime?: string;
  creationDateTime: string;
  apiKeyType?: string;
}

export interface IGenerateKeyProps {
  name: string;
  organizationId: string;
  applicationId?: string;
  apiKeyType?: string;
}

export interface IDeleteKeyRequest {
  organizationId: string;
  keyId: string;
}

export interface IKeysResponse {
  items: IKey[];
  pageIndex: number;
  pageSize: number;
  search: string;
  totalCount: number;
  filteredCount: number;
}

export const fetchKeys = async ({
  pageIndex,
  pageSize,
  search,
  type,
  organizationId,
}: IRequestParams): Promise<IKeysResponse> => {
  return await apiBase.get(`/organizations/${organizationId}/keys`, {
    params: {
      pageIndex: pageIndex ? pageIndex - 1 : 0,
      pageSize,
      search,
      type,
    },
  });
};

const generateKey = async (data: IGenerateKeyProps): Promise<IKey> => {
  const { name, organizationId, apiKeyType } = data;
  return await apiBase.post("keys", {
    name,
    organizationId,
    apiKeyType,
    applicationId: data.applicationId,
  });
};

const deleteKey = async ({
  organizationId,
  keyId,
}: IDeleteKeyRequest): Promise<{ status: string }> => {
  return await apiBase.delete("keys", {
    data: {
      organizationId,
      keyId,
    },
  });
};

const editKey = async (data: IEditQueryParams) => {
  const { organizationId, keyId, name, state } = data;
  return await apiBase.patch("keys", {
    organizationId,
    keyId,
    name,
    state,
  });
};

export const useKeys = (
  req: IRequestParams,
): UseQueryResult<IKeysResponse, unknown> => {
  return useQuery(
    [
      "apiKeys",
      req.organizationId,
      req.pageIndex,
      req.pageSize,
      req.search,
      req.type,
    ],
    async () => {
      const result: Promise<IKeysResponse> = fetchKeys(req);
      return result;
    },
    {
      enabled: !!req.organizationId,
    },
  );
};

export const useGenerateKey = () => {
  const queryClient = useQueryClient();

  return useMutation(generateKey, {
    onSuccess: () => {
      queryClient.invalidateQueries("apiKeys").catch(console.error);
      toast.success("Key generated successfully");
      AnalyticsGA.trackEvent("Key", "Generate", "Key generated successfully");
    },
  });
};

export const useDeleteKey = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteKey, {
    onSuccess: () => {
      queryClient.invalidateQueries("apiKeys").catch(console.error);
      toast.success("Key deleted successfully");
      AnalyticsGA.trackEvent("Key", "Delete", "Key deleted successfully");
    },
  });
};

export const useEditKey = () => {
  const queryClient = useQueryClient();

  return useMutation(editKey, {
    onSuccess: () => {
      queryClient.invalidateQueries("apiKeys").catch(console.error);
      toast.success("Key updated successfully");
      AnalyticsGA.trackEvent("Key", "Update", "Key updated successfully");
    },
  });
};
